import { MarketType } from '@drift-labs/sdk';
import { MarketId } from '@drift/common';
import Link from 'src/components/primitives/Link/Link';
import { memo, useEffect, useState } from 'react';
import useMarketInfoDisplayData from '../../hooks/useMarketInfoDisplayData';
import StatDisplay from './StatDisplay';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import use24hMarketDataFromStore from 'src/hooks/use24hMarketDataFromStore';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { MARKET_STATS_BAR_CELL_LABEL_CLASSNAME } from 'src/components/MarketStatsBar/consts';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import { cj } from 'src/utils/classNames';
import useIsMarketFundingPaused from 'src/hooks/useIsMarketFundingPaused';
interface Props {
	selectedMarketId: MarketId;
	marketSymbol: string;
	textOnlyDisplay?: boolean;
	/**
	 * Forces the component to use the provided unit rather than that pulled from storage.
	 */
	fundingRateUnit?: FundingRateUnit;
}

export type FundingRateUnit = 'h' | 'y';
export const DEFAULT_FUNDING_RATE_UNIT: FundingRateUnit = 'h';

const PAYMENTS_PER_YEAR = 24 * 365.25;

function FundingRateCard({
	selectedMarketId,
	marketSymbol,
	textOnlyDisplay,
	fundingRateUnit: fundingRateUnitFromProps,
}: Props) {
	const marketData24H = use24hMarketDataFromStore({
		targetMarketType: MarketType.PERP,
		targetMarketIndex: selectedMarketId?.marketIndex,
	})?.[0];
	const isMobileScreenSize = useIsMobileScreenSize();

	const marketData = useMarketInfoDisplayData();

	const hasLoadedInitialData = marketData.hasLoadedInitialData;

	const [fundingRateUnitFromStorage] = useSyncLocalStorage<FundingRateUnit>(
		'fundingRateUnitPreference',
		DEFAULT_FUNDING_RATE_UNIT
	);

	const isFundingPaused = useIsMarketFundingPaused(selectedMarketId);

	const fundingRateUnit =
		fundingRateUnitFromProps ?? fundingRateUnitFromStorage;

	const longFundingRatePredicted =
		parseFloat(marketData.longFundingRate) *
		(fundingRateUnit === 'h' ? 1 : PAYMENTS_PER_YEAR);

	const shortFundingRatePredicted =
		parseFloat(marketData.shortFundingRate) *
		(fundingRateUnit === 'h' ? 1 : PAYMENTS_PER_YEAR);

	const [fundingRate24H, setFundingRate24H] = useState({ long: 0, short: 0 });

	useEffect(() => {
		const getAndSetFundingData = async () => {
			if (!marketData24H) return;

			const multiplier = fundingRateUnit === 'h' ? 1 : PAYMENTS_PER_YEAR;

			setFundingRate24H({
				long: (marketData24H.avgLongFunding ?? 0) * multiplier,
				short: (marketData24H.avgShortFunding ?? 0) * multiplier,
			});
		};

		setFundingRate24H({ long: 0, short: 0 });

		getAndSetFundingData();

		const interval = setInterval(getAndSetFundingData, 1000 * 60);

		return () => {
			clearInterval(interval);
		};
	}, [fundingRateUnit, marketData24H]);

	const fundingRatePredictedToUse =
		Math.abs(longFundingRatePredicted) >= Math.abs(shortFundingRatePredicted)
			? longFundingRatePredicted
			: shortFundingRatePredicted;

	const fundingRate24HToUse =
		Math.abs(fundingRate24H.long) >= Math.abs(fundingRate24H.short)
			? fundingRate24H.long
			: fundingRate24H.short;

	// Used in market details drawer. There we show the rate adjusted for the unit.
	if (textOnlyDisplay) {
		if (isFundingPaused) {
			return (
				<span className="inline-flex items-center text-star-yellow">
					Paused
				</span>
			);
		}

		const fundingRatePredictedFormatted = `${fundingRatePredictedToUse.toFixed(
			fundingRateUnit == 'h' ? 5 : 2
		)}%`;

		const fundingRate24HFormatted = `${
			fundingRate24HToUse.toFixed(fundingRateUnit == 'h' ? 5 : 2) + '%'
		}`;

		return (
			<span className="inline-flex items-center">{`${fundingRatePredictedFormatted} / ${fundingRate24HFormatted}`}</span>
		);
	}

	const fundingRatePredictedAprFormatted = `${(
		fundingRatePredictedToUse * PAYMENTS_PER_YEAR
	).toFixed(2)}%`;
	const fundingRate24HAprFormatted = `${(
		fundingRate24HToUse * PAYMENTS_PER_YEAR
	).toFixed(2)}%`;

	const { header, displayValue, minWidth, maxWidth } = (() => {
		const displayValue = isFundingPaused
			? 'Paused'
			: `${fundingRatePredictedAprFormatted} / ${fundingRate24HAprFormatted}`;

		if (isMobileScreenSize) {
			return {
				header: 'Funding / 24h (APR)',
				displayValue,
				minWidth: '80px',
			};
		}

		return {
			header: 'Funding / Last 24h (APR)',
			displayValue,
			maxWidth: '180px',
		};
	})();

	const countdownMinutes = `${Math.floor(
		marketData?.estimatedFundingRateCountdownTs / 60
	)}`.padStart(2, '0');
	const countdownSeconds = `${Math.floor(
		marketData?.estimatedFundingRateCountdownTs % 60
	)}`.padStart(2, '0');

	const fundingRateCountdown = `${countdownMinutes}:${countdownSeconds}`;

	const fundingRatePredictedFormatted = `${fundingRatePredictedToUse.toFixed(
		5
	)}%`;
	const fundingRate24HFormatted = `${fundingRate24HToUse.toFixed(5)}%`;

	return (
		<Tooltip
			decoration="cursor-help"
			// Used in the market stats bar, where we hardcode the unit to 1h.
			// As such, we need to multiply by `PAYMENTS_PER_YEAR` to get the APR.
			content={
				isFundingPaused ? (
					<div>This market&apos;s funding has been paused</div>
				) : (
					<div className="flex flex-col gap-y-md">
						<div className="flex flex-col gap-y-sm">
							<div className="flex justify-between">
								<span>Funding Rate / APR</span>
								<span className="text-text-default">
									{fundingRatePredictedFormatted} /{' '}
									{fundingRatePredictedAprFormatted}
								</span>
							</div>
							<div className="flex justify-between">
								<span>Last 24h / APR</span>
								<span className="text-text-default">
									{fundingRate24HFormatted} / {fundingRate24HAprFormatted}
								</span>
							</div>
							<div className="flex justify-between">
								<span>Funding Countdown</span>
								<span className="text-text-default">
									{fundingRateCountdown}
								</span>
							</div>
						</div>
						Funding rate is 1/24th the average hourly premium calculated every
						hour. If positive, longs pay shorts. If negative, shorts pay longs.
						<Link href={`/stats/funding-history?market=${marketSymbol}`}>
							View stats
						</Link>
					</div>
				)
			}
		>
			<div className="hidden flex-col xs:flex" style={{ minWidth, maxWidth }}>
				<span className="cursor-help xs:text-wrap">
					<StatDisplay
						header={header}
						headerClassName={MARKET_STATS_BAR_CELL_LABEL_CLASSNAME}
						className={cj(
							'whitespace-nowrap',
							isFundingPaused && 'text-star-yellow'
						)}
						isLoading={!hasLoadedInitialData}
					>
						{displayValue}
					</StatDisplay>
				</span>
			</div>
		</Tooltip>
	);
}

export default memo(FundingRateCard);
